import { frontendConfig, middlewareApiUrl } from './config.js'

/** Client for the FE integration contract at /docs/fe-integration. */
export type MiddlewareApiPaths = {
  webviewInit: string
  submitIdentity: string
  submitFace: string
  submitEkycResult: string
  signContract: string
  contract: string
  upload: string
}

export type MiddlewareRequestOptions = {
  token?: string
  signal?: AbortSignal
}

export class MiddlewareApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message)
  }
}

const defaultPaths: MiddlewareApiPaths = {
  webviewInit: '/api/v1/webview/init',
  submitIdentity: '/api/v1/webview/submit-identity',
  submitFace: '/api/v1/webview/submit-face',
  submitEkycResult: '/api/v1/webview/submit-ekyc-result',
  signContract: '/api/v1/webview/sign-contract',
  contract: '/api/v1/webview/contract/:sessionId',
  upload: '/api/v1/webview/upload',
}

function paths(): MiddlewareApiPaths {
  const config = frontendConfig()
  return { ...defaultPaths, ...(config.middlewarePaths || {}) }
}

async function request(path: string, init: RequestInit = {}, options: MiddlewareRequestOptions = {}): Promise<unknown> {
  const headers = new Headers(init.headers)
  headers.set('accept', 'application/json')
  if (init.body && !(init.body instanceof FormData) && !headers.has('content-type')) {
    headers.set('content-type', 'application/json')
  }
  const config = frontendConfig()
  const token = options.token || config.middlewareWebviewToken
  if (token) headers.set('authorization', `Bearer ${token}`)

  const response = await fetch(middlewareApiUrl(path, config), { ...init, headers, signal: options.signal })
  const body: any = await response.json().catch(() => null)
  if (!response.ok || (body && body.status === 'error')) {
    const errorCode =
      body && typeof body.code === 'string'
        ? body.code
        : body && typeof body.error_code === 'string'
          ? body.error_code
          : 'HTTP_ERROR'
    throw new MiddlewareApiError(
      body && typeof body.message === 'string' ? body.message : 'Không thể kết nối EKYC Middleware API.',
      response.status,
      errorCode,
    )
  }
  return body
}

export const middlewareApi = {
  createTestSession(payload?: Record<string, unknown>, options?: MiddlewareRequestOptions) {
    return request('/api/v1/partner/test-session', {
      method: 'POST',
      body: JSON.stringify({
        user_id: `user_${Math.floor(Math.random() * 1000000)}`,
        partner_code: 'AT',
        identity_info: {
          email: `user${Math.floor(Math.random() * 1000000)}@test.com`,
          full_name: 'Nguyễn Văn A',
        },
        ...payload,
      }),
    }, options)
  },
  initWebview(
    sessionId: string,
    payloadOrOptions?: Record<string, unknown> | MiddlewareRequestOptions,
    options?: MiddlewareRequestOptions,
  ) {
    const isPayload = payloadOrOptions && !('token' in payloadOrOptions || 'signal' in payloadOrOptions)
    const payload = isPayload ? (payloadOrOptions as Record<string, unknown>) : {}
    const opts = (isPayload ? options : (payloadOrOptions as MiddlewareRequestOptions)) || {}

    return request(
      paths().webviewInit,
      {
        method: 'POST',
        body: JSON.stringify({
          ...payload,
        }),
      },
      opts,
    )
  },
  submitIdentity(sessionId: string, identityData: Record<string, unknown>, options?: MiddlewareRequestOptions) {
    return request(paths().submitIdentity, {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, identity_data: identityData }),
    }, options)
  },
  submitFace(sessionId: string, faceData: Record<string, unknown>, options?: MiddlewareRequestOptions) {
    return request(paths().submitFace, {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, face_data: faceData }),
    }, options)
  },
  submitEkycResult(sessionId: string, result: Record<string, unknown>, options?: MiddlewareRequestOptions) {
    return request(paths().submitEkycResult, {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, core_ekyc_result: result }),
    }, options)
  },
  getContracts(options?: MiddlewareRequestOptions) {
    return request('/api/v1/webview/contracts', {}, options)
  },
  upload(file: Blob | File, options?: MiddlewareRequestOptions) {
    const formData = new FormData()
    formData.append('file', file)
    return request(
      paths().upload,
      {
        method: 'POST',
        body: formData,
      },
      options,
    )
  },
}
